<script setup>
import { reactive, computed, watch, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const isEditing = computed(() => !!props.campaign);

const formState = {
  uiFlags: useMapGetter('whatsappCampaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  contactLists: useMapGetter('contactLists/getContactLists'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  macros: useMapGetter('macros/getMacros'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const buildInitialState = () => {
  if (props.campaign) {
    const c = props.campaign;

    // WhatsappCampaign: contact_list_id; Legacy Campaign: audience array
    let contactListIds = [];
    if (c.contact_list_id) {
      contactListIds = [c.contact_list_id];
    } else if (c.contact_list?.id) {
      contactListIds = [c.contact_list.id];
    } else {
      const audience = c.audience || [];
      contactListIds = audience
        .filter(a => a.type === 'ContactList')
        .map(a => a.id);
    }

    const labelIds = (c.audience || [])
      .filter(a => a.type === 'Label')
      .map(a => a.id);

    // WhatsappCampaign: start_date (YYYY-MM-DD); Legacy: scheduled_at (unix)
    let scheduledAt = null;
    if (c.start_date) {
      // start_date is "YYYY-MM-DD", combine with start_time for datetime-local
      const time = c.start_time || '08:00';
      scheduledAt = `${c.start_date}T${time}`;
    } else if (c.scheduled_at) {
      const d = new Date(c.scheduled_at * 1000);
      const offset = d.getTimezoneOffset();
      const local = new Date(d.getTime() - offset * 60000);
      scheduledAt = local.toISOString().slice(0, 16);
    }

    // WhatsappCampaign: send_interval (seconds); Legacy: send_interval_minutes
    const sendInterval = c.send_interval_minutes
      || (c.send_interval ? Math.round(c.send_interval / 60) : 0);

    // WhatsappCampaign: start_time/end_time; Legacy: sending_start_time/sending_end_time
    const startTime = c.sending_start_time || c.start_time || '08:00';
    const endTime = c.sending_end_time || c.end_time || '18:00';

    return {
      title: c.title || '',
      inboxId: c.inbox?.id || c.inbox_id || null,
      templateId: null,
      macroId: c.macro_id || null,
      funnelStageId: c.funnel_stage_id || null,
      scheduledAt,
      sendIntervalMinutes: sendInterval,
      sendingStartTime: startTime,
      sendingEndTime: endTime,
      selectedLabels: labelIds,
      selectedContactLists: contactListIds,
      audienceType: contactListIds.length > 0 ? 'contactLists' : 'labels',
    };
  }
  return {
    title: '',
    inboxId: null,
    templateId: null,
    macroId: null,
    funnelStageId: null,
    scheduledAt: null,
    sendIntervalMinutes: 0,
    sendingStartTime: '08:00',
    sendingEndTime: '18:00',
    selectedLabels: [],
    selectedContactLists: [],
    audienceType: 'labels',
  };
};

const state = reactive(buildInitialState());
const templateParserRef = ref(null);

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: isEditing.value ? {} : { required },
  scheduledAt: { required },
  selectedLabels: {},
  selectedContactLists: {},
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const labelOptions = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const contactListOptions = computed(
  () =>
    formState.contactLists.value?.map(list => ({
      value: list.id,
      label: `${list.name} (${list.contacts_count || 0} contatos)`,
    })) ?? []
);

const hasNoAudienceSelected = computed(
  () =>
    state.selectedLabels.length === 0 && state.selectedContactLists.length === 0
);

const macroOptions = computed(() => [
  { value: null, label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.MACRO.PLACEHOLDER') },
  ...mapToOptions(formState.macros.value, 'id', 'name'),
]);

const savedTemplateName = computed(() => {
  return props.campaign?.template_params?.name || null;
});

const savedProcessedParams = computed(() => {
  if (!props.campaign?.template_params?.processed_params) return null;
  // Only restore saved params when the selected template matches the saved one
  const currentTemplate = selectedTemplate.value;
  if (currentTemplate?.name !== savedTemplateName.value) return null;
  return props.campaign.template_params.processed_params;
});

const funnelStages = ref([]);

async function fetchFunnelStages() {
  await store.dispatch('funnels/fetchFunnels');
  const funnels = store.getters['funnels/getFunnels'] || [];
  const stages = [];
  funnels.forEach(funnel => {
    (funnel.stages || []).forEach(stage => {
      stages.push({
        value: stage.id,
        label: `${funnel.title} → ${stage.name}`,
      });
    });
  });
  funnelStages.value = stages;
}

const funnelStageOptions = computed(() => [
  { value: null, label: 'Nenhuma (não mover)' },
  ...funnelStages.value,
]);

onMounted(() => {
  store.dispatch('contactLists/get');
  store.dispatch('macros/get');
  store.dispatch('inboxes/get');
  fetchFunnelStages();
});

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: hasNoAudienceSelected.value
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.isFormInvalid === false;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    (hasNoAudienceSelected.value && !aiGenerating.value && !aiResult.value)
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, buildInitialState());
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  // Build audience array with both labels and contact lists
  const audience = [
    ...state.selectedLabels.map(id => ({ id, type: 'Label' })),
    ...state.selectedContactLists.map(id => ({ id, type: 'ContactList' })),
  ];

  const details = {
    title: state.title,
    inbox_id: state.inboxId,
    macro_id: state.macroId || null,
    funnel_stage_id: state.funnelStageId || null,
    scheduled_at: formatToUTCString(state.scheduledAt),
    send_interval_minutes: Number(state.sendIntervalMinutes) || 0,
    sending_start_time: state.sendingStartTime,
    sending_end_time: state.sendingEndTime,
    audience,
  };

  // Only include template data when a template is selected (new or changed)
  if (currentTemplate) {
    details.message = templateContent;
    details.template_params = templateParams;
  }

  return details;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !hasRequiredTemplateParams.value) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const isSyncingTemplates = ref(false);

const syncTemplates = async () => {
  if (!state.inboxId) return;
  isSyncingTemplates.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', state.inboxId);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

const hasInitializedTemplate = ref(false);

// Reset template selection when inbox changes (but not on initial edit load)
watch(
  () => state.inboxId,
  () => {
    if (hasInitializedTemplate.value) {
      state.templateId = null;
    }
    hasInitializedTemplate.value = true;
  }
);

// When template options change (loaded async), try to match saved template
watch(
  () => templateOptions.value,
  options => {
    if (
      isEditing.value &&
      !state.templateId &&
      savedTemplateName.value &&
      options.length > 0
    ) {
      const match = options.find(
        opt => opt.template?.name === savedTemplateName.value
      );
      if (match) {
        state.templateId = match.value;
      }
    }
  },
  { immediate: true }
);

// AI Lead Generation
const aiPrompt = ref('');
const aiNotifyPhone = ref('');
const aiGenerating = ref(false);
const aiProgressMessage = ref('');
const aiResult = ref(null);
const aiError = ref('');
const showAiPanel = ref(false);

const handleLeadGenStarted = data => {
  aiGenerating.value = true;
  aiProgressMessage.value = data.message || 'Iniciando...';
  aiError.value = '';
};

const handleLeadGenProgress = data => {
  aiProgressMessage.value = data.message || 'Processando...';
};

const handleLeadGenCompleted = data => {
  aiGenerating.value = false;
  aiProgressMessage.value = '';
  aiResult.value = data;
  useAlert(`Lista "${data.contact_list_name}" criada com ${data.contacts_count} contatos!`);

  // Auto-select the generated list
  if (data.contact_list_id && !state.selectedContactLists.includes(data.contact_list_id)) {
    state.selectedContactLists.push(data.contact_list_id);
  }
};

const handleLeadGenError = data => {
  aiGenerating.value = false;
  aiProgressMessage.value = '';
  aiError.value = data.error || 'Erro desconhecido na geração de leads.';
  useAlert(data.error || 'Erro na geração de leads.');
};

onMounted(() => {
  emitter.on('lead_generation.started', handleLeadGenStarted);
  emitter.on('lead_generation.progress', handleLeadGenProgress);
  emitter.on('lead_generation.completed', handleLeadGenCompleted);
  emitter.on('lead_generation.error', handleLeadGenError);
});

onUnmounted(() => {
  emitter.off('lead_generation.started', handleLeadGenStarted);
  emitter.off('lead_generation.progress', handleLeadGenProgress);
  emitter.off('lead_generation.completed', handleLeadGenCompleted);
  emitter.off('lead_generation.error', handleLeadGenError);
});

const generateLeads = async () => {
  if (!aiPrompt.value.trim()) {
    useAlert('Digite um prompt descrevendo o público-alvo.');
    return;
  }

  aiGenerating.value = true;
  aiError.value = '';
  aiResult.value = null;
  aiProgressMessage.value = 'Enviando pedido...';

  try {
    const parserData = templateParserRef.value;
    const templateContent = parserData?.renderedTemplate || '';

    await WhatsappCampaignsAPI.generateLeads({
      prompt: aiPrompt.value,
      campaign_message: templateContent,
      campaign_title: state.title || 'Campanha WhatsApp',
      notify_phone: aiNotifyPhone.value.trim() || null,
    });

    aiProgressMessage.value =
      'Processando em segundo plano... A IA está analisando seus contatos.';
  } catch {
    aiGenerating.value = false;
    aiProgressMessage.value = '';
    aiError.value = 'Erro ao iniciar geração de leads. Tente novamente.';
  }
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <div class="mt-1 flex items-center justify-between">
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
        </p>
        <Button
          v-if="state.inboxId"
          variant="faded"
          color="slate"
          size="small"
          type="button"
          :label="t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON')"
          :is-loading="isSyncingTemplates"
          class="shrink-0"
          @click="syncTemplates"
        />
      </div>
      <p
        v-if="isEditing && savedTemplateName && !state.templateId"
        class="mt-1 text-xs text-n-blue-11"
      >
        Template salvo: {{ savedTemplateName.replace(/_/g, ' ') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      :initial-params="savedProcessedParams"
    />

    <!-- Audience Section -->
    <div class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-black2">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </p>

      <!-- Labels Selection -->
      <div class="flex flex-col gap-1">
        <label for="labels" class="text-xs font-medium text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABELS_LABEL') ||
            'Etiquetas'
          }}
        </label>
        <TagMultiSelectComboBox
          v-model="state.selectedLabels"
          :options="labelOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABELS_PLACEHOLDER') ||
            'Selecionar etiquetas dos clientes'
          "
          class="[&>div>button]:bg-n-solid-2"
        />
      </div>

      <!-- Contact Lists Selection -->
      <div class="flex flex-col gap-1">
        <label for="contactLists" class="text-xs font-medium text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LISTS_LABEL') ||
            'Listas de Contatos'
          }}
        </label>
        <TagMultiSelectComboBox
          v-model="state.selectedContactLists"
          :options="contactListOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LISTS_PLACEHOLDER') ||
            'Selecionar listas de contatos'
          "
          class="[&>div>button]:bg-n-solid-2"
        />
      </div>

      <!-- Error message -->
      <p v-if="formErrors.audience" class="text-xs text-n-ruby-9">
        {{ formErrors.audience }}
      </p>

      <!-- AI Lead Generation -->
      <div class="border-t border-n-weak pt-3">
        <button
          type="button"
          class="flex items-center gap-2 text-sm font-medium text-n-blue-11 hover:text-n-blue-12 transition-colors"
          @click="showAiPanel = !showAiPanel"
        >
          <span class="i-lucide-sparkles text-base" />
          Gerar Lista com IA
          <span
            class="text-xs transition-transform"
            :class="showAiPanel ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>

        <div v-if="showAiPanel" class="mt-3 flex flex-col gap-3">
          <p class="text-xs text-n-slate-11">
            Descreva o perfil de leads desejado. A IA vai analisar seus contatos,
            conversas e funil para encontrar os melhores candidatos.
          </p>
          <textarea
            v-model="aiPrompt"
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 p-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-blue-7 resize-y min-h-[80px]"
            placeholder="Ex: Buscar contatos que tiveram conversas nos últimos 30 dias com valor acima de R$500 no funil de vendas. Limite: 100 leads."
            :disabled="aiGenerating"
          />

          <Input
            v-model="aiNotifyPhone"
            label="Receber resultado por WhatsApp (opcional)"
            placeholder="(11) 99999-9999"
            :disabled="aiGenerating"
          />
          <p class="text-xs text-n-slate-10 -mt-2">
            Ao informar seu telefone, você receberá o resultado da análise por
            WhatsApp quando o processamento terminar.
          </p>

          <Button
            type="button"
            variant="faded"
            color="blue"
            icon="i-lucide-sparkles"
            label="Gerar Lista"
            :is-loading="aiGenerating"
            :disabled="aiGenerating || !aiPrompt.trim()"
            @click="generateLeads"
          />

          <!-- Progress -->
          <div
            v-if="aiGenerating"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-blue-3 text-n-blue-11"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-loader-2 animate-spin" />
              <span class="text-sm">{{ aiProgressMessage }}</span>
            </div>
            <p class="text-xs text-n-blue-10">
              O processamento ocorre em segundo plano. Você pode fechar este
              formulário e a lista aparecerá automaticamente quando pronta.
            </p>
          </div>

          <!-- Error -->
          <div
            v-if="aiError"
            class="flex items-center gap-2 p-3 rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="i-lucide-alert-circle" />
            <span class="text-sm">{{ aiError }}</span>
          </div>

          <!-- Result -->
          <div
            v-if="aiResult"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-green-3 border border-n-green-6"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-check-circle text-n-green-11" />
              <span class="text-sm font-medium text-n-green-11">
                Lista "{{ aiResult.contact_list_name }}" criada com
                {{ aiResult.contacts_count }} contatos
              </span>
            </div>
            <p
              v-if="aiResult.ai_analysis"
              class="text-xs text-n-slate-11 mt-1 leading-relaxed"
            >
              <span class="font-medium text-n-slate-12">Análise da IA:</span>
              {{ aiResult.ai_analysis }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <Input
        v-model="state.sendIntervalMinutes"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_INTERVAL.LABEL')"
        type="number"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_INTERVAL.PLACEHOLDER')
        "
        min="0"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_INTERVAL.HELP') }}
      </p>
    </div>

    <!-- Macro Selection -->
    <div class="flex flex-col gap-1">
      <label for="macro" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MACRO.LABEL') }}
      </label>
      <ComboBox
        id="macro"
        v-model="state.macroId"
        :options="macroOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MACRO.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MACRO.HELP') }}
      </p>
    </div>

    <!-- Funnel Stage Selection -->
    <div class="flex flex-col gap-1">
      <label
        for="funnelStage"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        Etapa do Kanban
      </label>
      <ComboBox
        id="funnelStage"
        v-model="state.funnelStageId"
        :options="funnelStageOptions"
        placeholder="Selecione uma etapa (opcional)"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="text-xs text-n-slate-11">
        Ao enviar a mensagem, o contato será copiado ou movido para esta etapa
        do Kanban.
      </p>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Sending Time Window -->
    <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-black2">
      <p class="text-sm font-medium text-n-slate-12">Janela de Envio Diária</p>
      <p class="text-xs text-n-slate-11">
        O envio será pausado fora deste horário e retomado automaticamente no
        próximo dia.
      </p>
      <div class="flex gap-3">
        <Input
          v-model="state.sendingStartTime"
          label="Início"
          type="time"
          class="flex-1"
        />
        <Input
          v-model="state.sendingEndTime"
          label="Fim"
          type="time"
          class="flex-1"
        />
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="
          isEditing
            ? 'Salvar'
            : t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')
        "
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
